import React, { useEffect, useState } from "react";
import { View, ScrollView, StyleSheet, Alert, TouchableOpacity } from "react-native";
import { useLocalSearchParams, router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import ThemedInput from "@/components/ThemedInput";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { getEntityPermissions } from "@/lib/permissions";
import { unionService } from "@/services/unionService";
import { userService } from "@/services/userService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { UserPicker } from "@/components/Pickers/UserPicker";
import { TermsForm } from "@/components/screens/shared/TermsForm";
import { UnionType } from "@/types/db/unions";

export default function UnionEditPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const { userRoles, tenantId } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unionData, setUnionData] = useState<UnionType | null>(null);
  const [editedUnion, setEditedUnion] = useState<UnionType | null>(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "users" | "terms">("overview");

  const [users, setUsers] = useState<any[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);

  const permissions = getEntityPermissions(userRoles, "unions");

  // Load union data
  useEffect(() => {
    if (id) {
      loadUnionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUnionData = async () => {
    try {
      setLoading(true);
      const union = (await unionService.getUnionById(id!)) as UnionType & { unions_users?: any[] };
      setUnionData(union);
      setEditedUnion(union);

      // Load users from unions_users link table
      const userIds = (union.unions_users || []).map((uu) => uu.user_id);
      if (userIds.length > 0) {
        const unionUsers = await userService.getUsers({ userIds });
        setUsers(unionUsers);
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      toast.showToast({
        title: "Failed to load union",
        message: error?.message || "Unable to load union data",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editedUnion || !id || !tenantId) return;

    setSaving(true);
    try {
      // Save union data (name, description) - strip out unions_users relation
      const { unions_users, ...unionDataToSave } = editedUnion as any;
      await unionService.updateUnion(id, unionDataToSave);

      // Sync users in link table
      const userIds = users.map((u) => u.id!);
      await unionService.syncUnionUsers(id, tenantId, userIds);

      setUnionData(editedUnion);
      setEditing(false);

      toast.showToast({
        title: "Union updated",
        message: "Union has been successfully updated",
        toastType: "success",
      });

      await loadUnionData();
    } catch (error: any) {
      toast.showToast({
        title: "Update failed",
        message: error?.message || "Failed to update union",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedUnion(unionData);
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!unionData?.id) return;

    Alert.alert("Delete Union", `Are you sure you want to delete "${unionData.name}"? This action cannot be undone.`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          try {
            await unionService.deleteUnionsByIds([unionData.id]);
            toast.showToast({
              title: "Union deleted",
              message: "Union has been successfully deleted",
              toastType: "success",
            });
            router.push("/(app)/settings/unions");
          } catch (error: any) {
            toast.showToast({
              title: "Delete failed",
              message: error?.message || "Failed to delete union",
              toastType: "error",
            });
          }
        },
      },
    ]);
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <ThemedText>Loading union data...</ThemedText>
      </View>
    );
  }

  if (!unionData) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.dangerText} />
        <ThemedText style={{ marginTop: 12 }}>Union not found</ThemedText>
        <Button text="Go Back" onPress={() => router.push("/(app)/settings/unions")} style={{ marginTop: 16 }} />
      </View>
    );
  }

  const userCount = users.length;

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <TouchableOpacity onPress={() => router.push("/(app)/settings/unions")} style={styles.backButton}>
              <MaterialCommunityIcons name="arrow-left" size={24} color={colors.text} />
            </TouchableOpacity>
            <View style={styles.headerInfo}>
              <ThemedText type="title">{unionData.name}</ThemedText>
              {unionData.description && (
                <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>{unionData.description}</ThemedText>
              )}
            </View>
          </View>

          <View style={styles.headerActions}>
            {editing ? (
              <>
                <Button
                  text="Cancel"
                  onPress={handleCancel}
                  style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
                  textStyle={{ color: colors.text }}
                />
                <Button
                  text={saving ? "Saving..." : "Save"}
                  onPress={handleSave}
                  disabled={saving}
                  style={[styles.actionButton, { backgroundColor: colors.primary }]}
                  textStyle={{ color: "white" }}
                />
              </>
            ) : (
              <>
                {permissions.update && (
                  <Button
                    text="Edit"
                    iconPosition="right"
                    icon="pencil"
                    onPress={() => setEditing(true)}
                    style={[styles.actionButton, { backgroundColor: colors.primary }]}
                    textStyle={{ color: "white" }}
                  />
                )}
                {permissions.delete && (
                  <Button
                    text="Delete"
                    iconPosition="right"
                    icon="delete"
                    onPress={handleDelete}
                    style={[styles.actionButton, { backgroundColor: colors.red.background }]}
                    textStyle={{ color: "white" }}
                  />
                )}
              </>
            )}
          </View>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={[styles.tabNav, { borderBottomColor: colors.border }]}>
        {[
          { key: "overview", label: "Overview" },
          { key: "users", label: `Users (${userCount})` },
          { key: "terms", label: "Terms" },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && { borderBottomColor: colors.primary }]}
            onPress={() => setActiveTab(tab.key as any)}
          >
            <ThemedText style={[styles.tabText, activeTab === tab.key && { color: colors.primary }]}>
              {tab.label}
            </ThemedText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab Content */}
      <ScrollView style={styles.tabContent}>
        {activeTab === "overview" && (
          <View style={styles.section}>
            {editing && editedUnion ? (
              <>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Union Name</ThemedText>
                  <ThemedInput
                    value={editedUnion.name}
                    onChangeText={(text) => setEditedUnion({ ...editedUnion, name: text })}
                    style={styles.input}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Description</ThemedText>
                  <ThemedInput
                    value={editedUnion.description || ""}
                    onChangeText={(text) => setEditedUnion({ ...editedUnion, description: text })}
                    multiline
                    numberOfLines={3}
                    style={styles.input}
                  />
                </View>
              </>
            ) : (
              <>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Name</ThemedText>
                  <ThemedText>{unionData.name}</ThemedText>
                </View>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Description</ThemedText>
                  <ThemedText>{unionData.description || "No description"}</ThemedText>
                </View>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Members</ThemedText>
                  <ThemedText>{userCount} users</ThemedText>
                </View>
              </>
            )}
          </View>
        )}

        {activeTab === "users" && (
          <View style={styles.section}>
            {editing && (
              <Button
                text="Select Users"
                onPress={() => setShowUserPicker(true)}
                style={styles.pickerButton}
                icon="account-multiple"
              />
            )}
            {users.length > 0 ? (
              <View style={styles.userList}>
                {users.map((user) => {
                  const displayName = user.meta_data?.preferred_name || user.first_name;
                  const fullName = `${displayName} ${user.last_name}`.trim();

                  return (
                    <View key={user.id} style={[styles.userItem, { borderBottomColor: colors.border }]}>
                      <View style={styles.userInfo}>
                        <ThemedText type="defaultSemiBold">{fullName || "Unknown User"}</ThemedText>
                        {user.email_address && <ThemedText style={styles.userDetail}>{user.email_address}</ThemedText>}
                        {user.meta_data?.roles && user.meta_data.roles.length > 0 && (
                          <ThemedText style={styles.userRole}>Roles: {user.meta_data.roles.join(", ")}</ThemedText>
                        )}
                      </View>
                      {editing && (
                        <TouchableOpacity
                          onPress={() => {
                            const updatedUsers = users.filter((u) => u.id !== user.id);
                            setUsers(updatedUsers);
                          }}
                          style={styles.removeButton}
                        >
                          <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : (
              <ThemedText style={{ opacity: 0.5 }}>No users assigned</ThemedText>
            )}
          </View>
        )}

        {activeTab === "terms" && (
          <TermsForm
            terms={(editing ? editedUnion : unionData)?.meta_data?.terms || {}}
            onUpdateTerms={(newTerms) => {
              if (!editedUnion) return;
              setEditedUnion({
                ...editedUnion,
                meta_data: {
                  ...editedUnion.meta_data,
                  terms: newTerms,
                },
              });
            }}
            editing={editing}
            loading={loading}
          />
        )}
      </ScrollView>

      {/* User Picker Modal */}
      {showUserPicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <UserPicker
              multiple
              selectedUserIds={users.map((u) => u.id!)}
              onMultiPick={(selectedUsers) => {
                setUsers(selectedUsers);
                setShowUserPicker(false);
              }}
              onCancel={() => setShowUserPicker(false)}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  headerInfo: {
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  tabNav: {
    flexDirection: "row",
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "500",
  },
  tabContent: {
    flex: 1,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  inputGroup: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    opacity: 0.7,
    fontWeight: "500",
  },
  input: {
    marginBottom: 0,
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    alignItems: "center",
  },
  infoLabel: {
    opacity: 0.7,
    flex: 1,
  },
  termsSubsection: {
    marginBottom: 24,
    gap: 12,
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
    opacity: 0.9,
  },
  pickerButton: {
    alignSelf: "flex-start",
    marginBottom: 16,
  },
  userList: {
    gap: 0,
  },
  userItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  userDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  userRole: {
    fontSize: 11,
    opacity: 0.5,
    fontStyle: "italic",
  },
  removeButton: {
    padding: 4,
  },
  termFieldItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  termFieldContent: {
    flex: 1,
  },
  fieldRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flex: 1,
  },
  fieldInfo: {
    flex: 1,
    marginRight: 12,
  },
  fieldLabel: {
    fontSize: 14,
    opacity: 0.9,
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    padding: 2,
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    boxShadow: "0 2px 2.22px 0 rgba(0, 0, 0, 0.22)",
    elevation: 3,
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    width: "90%",
    maxWidth: 500,
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  addFieldButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderRadius: 8,
    borderStyle: "dashed",
    marginTop: 8,
  },
});
